import { useState, useRef, useEffect, useCallback } from 'react';
import { RealtimeClient } from '@openai/realtime-api-beta';
import { TunedWavRecorder } from '@/lib/audio/tuned-wav-recorder';
import { AECWavStreamPlayer } from '@/lib/audio/aec-wav-stream-player';
import { supabase } from '@/config/supabase';
import { notify } from '@/lib/notify';

/**
 * Realtime Doubt Hook (library-backed)
 *
 * Live audio is delegated to OpenAI's reference client +
 * `wavtools` so we don't have to maintain custom AudioWorklets,
 * scheduled-playback queues, autoplay-policy workarounds, or AEC
 * routing. The library handles:
 *   - mic capture via WavRecorder (built-in AudioWorklet)
 *   - gapless playback via WavStreamPlayer
 *   - barge-in via `conversation.interrupted` + `cancelResponse`
 *   - lifecycle/cleanup with no leak past disconnect
 *
 * The library connects to a WebSocket URL we provide - our backend
 * `/ws/doubt/{lesson_id}` is exactly the "relay server" pattern the
 * library documents. The wire format is identical (OpenAI Realtime
 * protocol over WS), so the backend's recording/persistence pipeline
 * works unchanged.
 *
 * One backend-side caveat: the library auto-sends a `session.update`
 * with its default config on connect. The backend filters that
 * event out of the client→OpenAI forward path so our authoritative
 * lesson-aware session config (instructions, server VAD, voice)
 * isn't overwritten with library defaults.
 */

interface UseRealtimeDoubtOptions {
  lessonId: string;
  currentTime: number;
  onSceneReceived: (scene: unknown[]) => void;
  onNarrationStart: () => void;
  onNarrationEnd: () => void;
}

const SAMPLE_RATE = 24000;

export function useRealtimeDoubt(options: UseRealtimeDoubtOptions) {
  const clientRef = useRef<RealtimeClient | null>(null);
  const recorderRef = useRef<TunedWavRecorder | null>(null);
  const playerRef = useRef<AECWavStreamPlayer | null>(null);

  const [isConnected, setIsConnected] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Stable refs to options callbacks so connect() doesn't re-run on
  // every parent render (the parent recreates these closures).
  const onSceneRef = useRef(options.onSceneReceived);
  const onNarrationStartRef = useRef(options.onNarrationStart);
  const onNarrationEndRef = useRef(options.onNarrationEnd);
  useEffect(() => {
    onSceneRef.current = options.onSceneReceived;
    onNarrationStartRef.current = options.onNarrationStart;
    onNarrationEndRef.current = options.onNarrationEnd;
  });

  const disconnect = useCallback(async () => {
    try {
      if (recorderRef.current) {
        try {
          await recorderRef.current.end();
        } catch {
          /* ignore */
        }
        recorderRef.current = null;
      }
      if (playerRef.current) {
        try {
          await playerRef.current.interrupt();
        } catch {
          /* ignore */
        }
        playerRef.current.cleanupAEC();
        playerRef.current = null;
      }
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
    } finally {
      setIsConnected(false);
      setIsPlaying(false);
      setIsRecording(false);
    }
  }, []);

  const connect = useCallback(async () => {
    try {
      const {
        data: { session },
        error: authError,
      } = await supabase.auth.getSession();
      if (authError || !session) {
        setError('Authentication required');
        notify.error('Please sign in to use doubt solving');
        return;
      }

      const wsBase =
        (import.meta.env.VITE_WS_URL as string | undefined) ||
        ((import.meta.env.VITE_API_URL as string | undefined)?.replace(
          /^http/,
          'ws'
        ) ??
          '');
      if (!wsBase) {
        console.error('No VITE_WS_URL or VITE_API_URL configured for doubt WS');
        setError('Doubt service not configured');
        return;
      }

      const url = `${wsBase}/ws/doubt/${options.lessonId}?timestamp=${options.currentTime}&token=${session.access_token}`;

      // 1. Build the realtime client (relay mode - no API key in browser).
      const client = new RealtimeClient({ url });
      clientRef.current = client;

      // 2. Wire up event handlers BEFORE connect() so we don't miss
      //    the library's initial events.
      client.on(
        'conversation.updated',
        ({
          item,
          delta,
        }: {
          item: { id: string; role?: string };
          delta?: { audio?: Int16Array };
        }) => {
          // CRITICAL: only play *assistant* audio. The library also
          // emits `conversation.updated` deltas for the user's own
          // captured speech - without the role check we'd echo the
          // user's voice through the speaker, churn `add16BitPCM`
          // tracks (one per delta = many UI re-renders), and feed
          // the loop the half-duplex gate is meant to prevent.
          if (item?.role !== 'assistant') return;
          if (delta?.audio && playerRef.current) {
            playerRef.current.add16BitPCM(delta.audio, item.id);
            setIsPlaying(true);
          }
        }
      );

      // NOTE: we do NOT call `wavStreamPlayer.interrupt()` here.
      //
      // The library dispatches `conversation.interrupted` on EVERY
      // `speech_started` event from the server (client.js:321), even
      // for tiny ambient noises that VAD picks up but that don't
      // actually result in the AI being cancelled. Calling
      // `interrupt()` marks the current track as interrupted, and
      // wavtools then *silently drops every future chunk for that
      // track* (wav_stream_player.js:106) - meaning a single false
      // VAD trigger cuts the AI off after ~3 words for the rest of
      // the response.
      //
      // Instead, let local playback drain naturally. If it's a real
      // barge-in, the server cancels the response, audio.delta
      // events stop arriving, and the player exhausts its queue
      // (~500 ms of tail audio - acceptable). If it's a false
      // trigger, the AI keeps streaming and the user keeps hearing
      // it as expected.
      client.on('conversation.interrupted', () => {
        // No-op. Server controls when audio actually stops; we just
        // let the buffered chunks finish.
      });

      client.on('error', (event: unknown) => {
        const ev = event as { message?: string; error?: { message?: string } };
        const msg =
          ev?.message ?? ev?.error?.message ?? 'Realtime connection error';
        console.error('Realtime error:', event);
        setError(msg);
        notify.error(msg);
      });

      // Custom backend events ride alongside standard OpenAI events
      // through `realtime.event`. We use this to bootstrap the
      // greeting on `session_ready`, render scenes / narration
      // markers, and (critically) interrupt local playback only when
      // the server confirms a response was actually cancelled.
      client.on(
        'realtime.event',
        ({
          source,
          event,
        }: {
          source: string;
          event: {
            type?: string;
            response?: { status?: string };
            [k: string]: unknown;
          };
        }) => {
          if (source !== 'server') return;
          switch (event.type) {
            case 'session_ready':
              client.createResponse();
              break;
            case 'scene_data':
              onSceneRef.current(
                (event.excalidraw_elements as unknown[]) ?? []
              );
              break;
            case 'narration.start':
              onNarrationStartRef.current();
              break;
            case 'narration.end':
              onNarrationEndRef.current();
              break;
            case 'response.done': {
              // The server emits `response.done` for every response,
              // with `status` describing how it ended. Only `cancelled`
              // / `incomplete` mean the user actually barged in (or
              // the server stopped early). For those, halt the local
              // player so the user doesn't keep hearing the tail of
              // buffered AI audio they already overruled with their
              // voice. For `completed` (natural finish), do nothing -
              // the queue drains on its own.
              const status = event.response?.status;
              if (status && status !== 'completed' && playerRef.current) {
                // interrupt() is synchronous (returns the interrupted track's
                // offset info), so there's no promise to catch here.
                playerRef.current.interrupt();
                setIsPlaying(false);
              }
              break;
            }
          }
        }
      );

      // 3. Open the WebSocket. We bypass `client.connect()` because
      //    the public `RealtimeClient.connect()` hardcodes a default
      //    `?model=gpt-4o-realtime-preview-...` query string and
      //    appends it with a literal `?` - so a relay URL that
      //    already has query params (our `?token=...&timestamp=...`)
      //    ends up with TWO `?` separators, mangling our token. By
      //    calling `realtime.connect({ model: '' })`, the falsy
      //    model skips the append (see lib/api.js line 76).
      //
      //    We also skip `client.updateSession()` (the second step of
      //    the high-level connect) - backend filters that anyway,
      //    and not sending it keeps the wire clean.
      await client.realtime.connect({ model: '' });
      setIsConnected(true);

      // 4. Set up audio. We're on the user gesture stack from the
      //    "Ask Doubt" click that mounted DoubtSession, which is what
      //    `getUserMedia` and `AudioContext` resume require.
      const player = new AECWavStreamPlayer({ sampleRate: SAMPLE_RATE });
      await player.connect();
      playerRef.current = player;

      const recorder = new TunedWavRecorder({ sampleRate: SAMPLE_RATE });
      recorderRef.current = recorder;
      try {
        await recorder.begin();
        setHasPermission(true);
        await recorder.record((data: { mono: Int16Array }) => {
          // Full-duplex: AEC at the player layer cancels the AI's
          // voice from the mic, so we can forward unconditionally.
          // Server VAD handles barge-in via `conversation.interrupted`.
          clientRef.current?.appendInputAudio(data.mono);
        });
        setIsRecording(true);
      } catch (err: unknown) {
        const e = err as Error;
        console.error('Mic permission/start failed:', e);
        setHasPermission(false);
        notify.error(
          'Microphone access is required for doubt solving' +
            (e?.message ? `: ${e.message}` : '')
        );
      }
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Failed to connect:', e);
      setError(e?.message ?? 'Connection failed');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.lessonId]);

  const startRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    try {
      const status = recorder.getStatus();
      if (status === 'paused') {
        await recorder.record((data: { mono: Int16Array }) => {
          clientRef.current?.appendInputAudio(data.mono);
        });
      } else if (status === 'ended') {
        await recorder.begin();
        setHasPermission(true);
        await recorder.record((data: { mono: Int16Array }) => {
          clientRef.current?.appendInputAudio(data.mono);
        });
      }
      setIsRecording(true);
    } catch (err: unknown) {
      const e = err as Error;
      console.error('Failed to start recording:', e);
      notify.error('Microphone error: ' + (e?.message ?? 'unknown'));
    }
  }, []);

  const stopRecording = useCallback(async () => {
    const recorder = recorderRef.current;
    if (!recorder) return;
    try {
      if (recorder.getStatus() === 'recording') {
        await recorder.pause();
      }
    } catch (err) {
      console.error('Failed to stop recording:', err);
    }
    setIsRecording(false);
  }, []);

  const requestPermission = useCallback(async () => {
    // Permission is requested by `recorder.begin()` inside connect();
    // by the time this is called, hasPermission is set. Kept on the
    // return shape so the consumer (DoubtSolvingCanvas) doesn't need
    // to care which path granted it.
    return hasPermission === true;
  }, [hasPermission]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    isConnected,
    error,
    isPlaying,
    isRecording,
    hasPermission,
    startRecording,
    stopRecording,
    requestPermission,
    isWarning: false,
  };
}
